import {
  AudioLines,
  Circle,
  Hand,
  Mic,
  MicOff,
  MonitorUp,
  Presentation,
  Settings,
  SmilePlus,
  Video,
  VideoOff,
} from 'lucide-react';
import { useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';

import { REACTION_EMOJIS, isRecordingActive } from '../realtime/frames';
import { LeaveMenu } from './LeaveMenu';
import { QuickSettingsPanel, QuickSettingsPopover } from './QuickSettingsPopover';
import { ToggleButton } from './ToggleButton';
import type { QuickSettingsBinding } from './ControlBar';
import { RecordingControls } from './RecordingControls';
import type { RecordingControlsSnapshot } from './RecordingControls';

/**
 * The webinar bottom bar. Staff (moderator/admin) get the mockup's host bar: mic/cam, the
 * recording toggle and - for admins - «Завершить для всех»; per-viewer speak actions live in
 * the «Зрители» tab where the host clicks a person, not a dropdown. Viewers get hand and
 * reactions, and the microphone button unlocks only with a speak grant - a granted speaker
 * also gets the camera toggle (the speak token publishes both). Both sides leave through the
 * same button.
 */
export interface WebinarControlBarProps {
  readonly isHost: boolean;
  readonly isAdmin: boolean;
  /**
   * The room's board-broadcast switch (the board surface amendment): true while the board is
   * the room's shared stage. Staff-only data - only hosts see and drive the toggle.
   */
  readonly boardOpen: boolean;
  /** Toggles the board broadcast for the whole room (staff only; server-authoritative). */
  readonly onToggleBoard: () => void;
  readonly micEnabled: boolean;
  readonly camEnabled: boolean;
  /** True while the local participant may not publish (viewer without a speak grant). */
  readonly micLocked: boolean;
  /**
   * True when the camera publish is not available (a viewer without a speak grant). Staff
   * publish by role, granted speakers by their token - both see the toggle.
   */
  readonly camLocked: boolean;
  /** True while the own screen share is live (the toggle's pressed/accent state). */
  readonly sharing: boolean;
  /** True while the screen-share toggle is in flight (browser picker / publish). */
  readonly screenBusy: boolean;
  /** True while the own hand is raised (optimistic + server state merged by the page). */
  readonly handRaised: boolean;
  readonly recording: RecordingControlsSnapshot | null;
  readonly recBusy: boolean;
  readonly endingForAll: boolean;
  readonly isMobile: boolean;
  readonly onToggleMic: () => void;
  readonly onToggleCam: () => void;
  readonly onToggleShare: () => void;
  /** Opens the effects dialog (microphone processing + camera background). */
  readonly onOpenEffects: () => void;
  /**
   * The quick-settings binding (personal devices, camera quality, background mode, the effects
   * entry). Personal settings: the gear renders for hosts, granted speakers AND locked viewers
   * - nothing behind it publishes anything.
   */
  readonly quickSettings?: QuickSettingsBinding | undefined;
  readonly onStartRec: () => void;
  readonly onStopRec: () => void;
  readonly onToggleHand: () => void;
  readonly onSendReaction: (emoji: string) => void;
  /** Opens the webinar side panel on mobile (the chat tab). */
  readonly onOpenPanel: () => void;
  readonly onLeave: () => void;
  readonly onEndForAll: () => void;
}

export function WebinarControlBar(props: WebinarControlBarProps): JSX.Element {
  const { t } = useTranslation();
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);

  return (
    <div className="relative shrink-0 border-t border-white/[0.06] px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-xl border border-white/[0.08] bg-white/[0.04] p-1">
          <ToggleButton
            testid="webinar-mic-toggle"
            label={t('webinar.mic_toggle_aria')}
            tooltip={
              props.micLocked
                ? t('webinar.mic_locked_title')
                : props.micEnabled
                  ? t('webinar.mic_on_aria')
                  : t('webinar.mic_off_aria')
            }
            icon={props.micEnabled ? Mic : MicOff}
            pressed={!props.micEnabled}
            variant="danger"
            disabled={props.micLocked}
            compact
            onClick={props.onToggleMic}
          />
          {!props.camLocked ? (
            <ToggleButton
              testid="webinar-cam-toggle"
              label={t('webinar.cam_toggle_aria')}
              icon={props.camEnabled ? Video : VideoOff}
              pressed={!props.camEnabled}
              variant="danger"
              compact
              onClick={props.onToggleCam}
            />
          ) : null}
          {!props.camLocked ? (
            // Publish-capable clients (staff, granted speakers) can put their screen on the
            // broadcast stage; locked viewers see no toggle, like the camera one.
            <ToggleButton
              testid="webinar-share-toggle"
              label={props.sharing ? t('controls.stop_share') : t('controls.share')}
              icon={MonitorUp}
              pressed={props.sharing}
              variant="accent"
              disabled={props.screenBusy}
              compact
              onClick={props.onToggleShare}
            />
          ) : null}
          {!props.camLocked ? (
            // The effects entry rides the same publish-capable audience as the camera and
            // share toggles: the effects apply to what THIS client publishes (mic processing,
            // camera background), so a locked viewer gets no button - the guard mirrors
            // mayPublishMedia in RoomPage and lifts exactly when the speak grant lands.
            <ToggleButton
              testid="webinar-effects"
              label={t('controls.effects')}
              icon={AudioLines}
              pressed={false}
              compact
              onClick={props.onOpenEffects}
            />
          ) : null}
          {props.quickSettings !== undefined ? (
            // Personal quick settings: unlike the effects entry this is NOT publish-gated -
            // a locked viewer picks their speaker and camera quality exactly like staff.
            <ToggleButton
              testid="webinar-quick-settings"
              label={t('controls.quick_settings')}
              icon={Settings}
              pressed={quickOpen}
              compact
              onClick={() => {
                setQuickOpen((open) => !open);
              }}
            />
          ) : null}
        </div>

        {!props.isHost ? (
          <>
            <button
              type="button"
              data-testid="webinar-hand-toggle"
              aria-pressed={props.handRaised}
              title={t('webinar.hand_toggle_aria')}
              aria-label={t('webinar.hand_toggle_aria')}
              onClick={props.onToggleHand}
              className={cn(
                'flex h-9 items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-[12.5px] font-medium transition-colors',
                props.handRaised
                  ? 'text-amber-300'
                  : 'text-zinc-300 hover:bg-white/10 hover:text-white',
              )}
            >
              <Hand className="size-3.5" />
              {t('webinar.hand_button')}
            </button>
            <div className="relative">
              <button
                type="button"
                data-testid="webinar-react-toggle"
                aria-pressed={reactionsOpen}
                aria-label={t('webinar.reactions_aria')}
                onClick={() => {
                  setReactionsOpen((open) => !open);
                }}
                className="flex h-9 items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-[12.5px] font-medium text-zinc-300 transition-colors hover:bg-white/10 hover:text-white"
              >
                <SmilePlus className="size-3.5" />
              </button>
              {reactionsOpen ? (
                <div
                  data-testid="webinar-react-picker"
                  className="absolute bottom-12 left-0 z-30 flex gap-1 rounded-xl border border-white/10 bg-zinc-900/95 p-1.5 shadow-xl backdrop-blur"
                >
                  {REACTION_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      data-testid={`webinar-react-option-${emoji}`}
                      onClick={() => {
                        props.onSendReaction(emoji);
                        setReactionsOpen(false);
                      }}
                      className="grid size-8 place-items-center rounded-lg text-lg transition-transform hover:scale-110 hover:bg-white/10"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </>
        ) : null}

        {props.isHost ? (
          // The board-broadcast toggle (the board surface amendment): the host opens the board
          // as the room's shared stage - every member sees it, cameras drop to the strip.
          <button
            type="button"
            data-testid="webinar-board-toggle"
            aria-pressed={props.boardOpen}
            aria-label={props.boardOpen ? t('webinar.board_hide') : t('webinar.board_show')}
            title={props.boardOpen ? t('webinar.board_hide') : t('webinar.board_show')}
            onClick={props.onToggleBoard}
            className={cn(
              'flex h-9 items-center gap-1.5 rounded-lg px-3 text-[12.5px] font-medium transition-colors',
              props.boardOpen
                ? 'bg-emerald-500 text-zinc-950 hover:bg-emerald-400'
                : 'border border-white/[0.08] bg-white/[0.04] text-zinc-300 hover:bg-white/10 hover:text-white',
            )}
          >
            <Presentation className="size-3.5" />
            {t('webinar.board_toggle')}
          </button>
        ) : null}

        {props.isHost ? (
          <RecordingControls
            recording={props.recording}
            canControl
            busy={props.recBusy}
            onStart={props.onStartRec}
            onStop={props.onStopRec}
          />
        ) : props.recording !== null && isRecordingActive(props.recording.state) ? (
          <span
            data-testid="webinar-rec-indicator"
            title={t(`recording.state_${props.recording.state}`)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-[11px] font-semibold tracking-wider text-red-400 uppercase"
          >
            <Circle aria-hidden className="size-2 animate-pulse fill-red-500 text-red-500" />
            {t('recording.banner')}
          </span>
        ) : null}

        {props.isMobile ? (
          <button
            type="button"
            data-testid="webinar-panel-toggle"
            aria-label={t('chat.tab')}
            onClick={props.onOpenPanel}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-[12.5px] font-medium text-zinc-300 transition-colors hover:bg-white/10 hover:text-white"
          >
            {t('chat.tab')}
          </button>
        ) : null}

        <div className="ml-auto flex items-center gap-2">
          {/* The ONE exit button (the unified room bars): «Выйти» always; for hosts (staff)
              the destructive end-for-everyone sits behind the inline confirm gate. */}
          <LeaveMenu
            canEndForAll={props.isHost}
            endingForAll={props.endingForAll}
            compact={props.isMobile}
            onLeave={props.onLeave}
            onEndForAll={props.onEndForAll}
          />
        </div>
      </div>

      {/* The popover anchors to the bar container (relative, added with this surface) so no
          overflow context can clip it; right-anchored above the leave/end actions. Mobile:
          the same panel as a full-width bottom sheet (the popover is cramped on phones). */}
      {props.quickSettings !== undefined && quickOpen ? (
        props.isMobile ? (
          <Sheet open onOpenChange={setQuickOpen}>
            <SheetContent
              data-testid="quick-settings-sheet"
              side="bottom"
              hideClose
              closeLabel={t('quick.close_aria')}
              aria-describedby={undefined}
              className="items-stretch rounded-t-2xl border-white/10 bg-zinc-900 sm:max-w-none"
            >
              <SheetTitle className="sr-only">{t('controls.quick_settings')}</SheetTitle>
              <QuickSettingsPanel
                state={props.quickSettings}
                actions={props.quickSettings}
                onClose={() => {
                  setQuickOpen(false);
                }}
                variant="sheet"
              />
            </SheetContent>
          </Sheet>
        ) : (
          <div className="absolute right-3 bottom-full z-30 mb-1">
            <QuickSettingsPopover
              state={props.quickSettings}
              actions={props.quickSettings}
              onClose={() => {
                setQuickOpen(false);
              }}
            />
          </div>
        )
      ) : null}
    </div>
  );
}
